import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useBlocker, useNavigate, type BlockerFunction, type NavigateOptions, type To } from "react-router";

import { ConfirmModal } from "@/components/ConfirmModal";

/**
 * A form that holds something the server does not must not lose it by accident. Every way out of the
 * page is held, ONCE, for the whole console, by the ``UnsavedChangesProvider`` the shell draws:
 *
 *   - a navigation inside the app (a sidebar entry, the way back, a tab of a section, the browser's
 *     Back button, a Cancel that goes back) is held by the router and the operator is asked, in a
 *     dialog: Stay, or Leave;
 *   - closing or reloading the tab, or leaving the console by any other address, gets the browser's
 *     own question (a page cannot draw its own there). The sign-in lives in memory, so a reload is
 *     also a sign-out: the warning matters twice;
 *   - "Sign out" asks the same question as a navigation does, and once (the browser's is set aside
 *     for it, so it is not asked twice). It is never asked for and then lost: if the operator also
 *     pressed Back while the question was open, "Leave" does both. If the sign-out FAILS the browser's
 *     question is back at once — the form is still there with what was typed in it — and the caller
 *     says why (`requestLeave` takes the promise the gesture returns).
 *
 * The router allows ONE blocker, and a page can hold several forms (a stage, and a card per
 * third-party provider under it): so the forms do not block anything themselves, they REPORT to the
 * provider whether they are dirty, and the provider blocks while any of them is. That is also why
 * this is a hook and a provider and not a ``useBlocker`` in each form.
 *
 * What is never held: the sign-in and sign-out addresses (``/auth/…``) — the operator can always get
 * out — and a change that stays on the page (only the hash changes, or nothing).
 *
 * The hook takes the form's own ``dirty`` (see ``useDirtyValues``: a form that has just loaded, or
 * whose fields were put back, is not dirty) and gives back ``leave``: the navigation to make once the
 * work is SAVED, which the form's own dirtiness must not hold — the form is about to go away, and
 * saved is not lost. Other forms of the page still hold it: what THEY hold is not saved by this one.
 */
interface Registry {
  report: (id: string, dirty: boolean) => void;
  withoutEntry: <T>(id: string, run: () => T) => T;
  requestLeave: (action: () => void | Promise<unknown>) => void;
}

// Outside a provider (there is none outside the shell) a form is simply not guarded.
const UNGUARDED: Registry = {
  report: () => {},
  withoutEntry: (_id, run) => run(),
  requestLeave: (action) => void action(),
};

const RegistryContext = createContext<Registry>(UNGUARDED);

/** Sign-in and sign-out: never held, whatever the forms hold. */
function isAuthAddress(pathname: string): boolean {
  return pathname === "/auth" || pathname.startsWith("/auth/");
}

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  // The forms that are dirty right now. A ref, read by the blocker at the moment a navigation is
  // attempted: the answer is always the latest, never the one of the render the blocker was made in.
  const dirtyForms = useRef(new Set<string>());
  // The same fact as state, for what must follow it (the browser's listener).
  const [holding, setHolding] = useState(false);
  // A gesture that leaves the console without going through the router ("Sign out"), waiting for the answer.
  const [pendingAction, setPendingAction] = useState<(() => void | Promise<unknown>) | null>(null);
  // The browser's own question is set aside for a departure the operator has just confirmed.
  const browserQuestionOff = useRef(false);
  // The navigation that is held comes right after a SAVE that worked (see ``withoutEntry``): what is
  // held is then not the form that saved but something else on the page, and the dialog says so —
  // "Leave without saving?" would read as if the save had failed.
  const afterSave = useRef(false);
  const [savedFirst, setSavedFirst] = useState(false);

  const report = useCallback((id: string, dirty: boolean) => {
    if (dirty) dirtyForms.current.add(id);
    else dirtyForms.current.delete(id);
    setHolding(dirtyForms.current.size > 0);
  }, []);

  // ``navigate`` asks the router synchronously: for the length of ``run`` the form that saved is not
  // among the dirty ones, and afterwards it is again if the navigation was held for another form.
  const withoutEntry = useCallback(<T,>(id: string, run: () => T): T => {
    const was = dirtyForms.current.delete(id);
    afterSave.current = true;
    try {
      return run();
    } finally {
      afterSave.current = false;
      if (was) dirtyForms.current.add(id);
    }
  }, []);

  // Run a gesture that leaves the console without the router. A rejection is the caller's to report (it
  // has the promise); it must not be left unhandled here, and it puts the browser's question back at
  // once when it had been set aside: the sign-out did not happen, the operator is still on the form.
  const depart = useCallback((action: () => void | Promise<unknown>, questionAlreadyAsked: boolean) => {
    let restore = () => {};
    if (questionAlreadyAsked) {
      // The page is going away and the operator has just said so: the browser must not ask a second
      // time. The delay is only a net, for a gesture that neither leaves nor fails.
      browserQuestionOff.current = true;
      const timer = window.setTimeout(() => {
        browserQuestionOff.current = false;
      }, 10_000);
      restore = () => {
        window.clearTimeout(timer);
        browserQuestionOff.current = false;
      };
    }
    try {
      const running = action();
      if (running && typeof running.then === "function") running.then(undefined, restore);
    } catch (error) {
      restore();
      throw error;
    }
  }, []);

  const requestLeave = useCallback(
    (action: () => void | Promise<unknown>) => {
      if (dirtyForms.current.size === 0) {
        depart(action, false);
        return;
      }
      setPendingAction(() => action);
    },
    [depart],
  );

  const shouldBlock = useCallback<BlockerFunction>(({ currentLocation, nextLocation }) => {
    if (dirtyForms.current.size === 0) return false;
    if (isAuthAddress(nextLocation.pathname)) return false;
    // The address of a page is its path and its query (``?cloneFrom=`` picks what an editor opens);
    // the hash only says where to scroll.
    const holds = currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search;
    if (holds) setSavedFirst(afterSave.current);
    return holds;
  }, []);
  const blocker = useBlocker(shouldBlock);

  // The browser's question, while (and only while) something is unsaved. Added and removed with the
  // fact, so a form that is saved, or left, leaves no listener behind (StrictMode runs the effect,
  // its cleanup, then the effect again: the result is one listener).
  useEffect(() => {
    if (!holding) return;
    function onBeforeUnload(event: BeforeUnloadEvent) {
      if (browserQuestionOff.current) return;
      event.preventDefault();
      // The standard says ``preventDefault`` is enough; the browsers that predate it want a value.
      event.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [holding]);

  const registry = useMemo<Registry>(() => ({ report, withoutEntry, requestLeave }), [report, withoutEntry, requestLeave]);

  const asking = blocker.state === "blocked" || pendingAction !== null;
  const showSavedFirst = savedFirst && blocker.state === "blocked";

  function stay() {
    if (blocker.state === "blocked") blocker.reset();
    setPendingAction(null);
    setSavedFirst(false);
  }

  function leave() {
    // Both can be waiting: a sign-out that was asked for, and a navigation held afterwards (Back, with
    // the question still open). "Leave" answers both — the navigation goes through, then the departure
    // that was asked for is made. A sign-out asked for and then dropped would be worse than either.
    const routeHeld = blocker.state === "blocked";
    const action = pendingAction;
    setPendingAction(null);
    setSavedFirst(false);
    if (routeHeld) blocker.proceed();
    if (action) depart(action, true);
  }

  return (
    <RegistryContext.Provider value={registry}>
      {children}
      {asking && (
        <ConfirmModal
          title={showSavedFirst ? "Saved — but some input is not saved" : "Leave without saving?"}
          confirmLabel="Leave"
          cancelLabel="Stay"
          danger
          onCancel={stay}
          onConfirm={leave}
        >
          <p>
            {showSavedFirst
              ? "Your changes were saved. Something else on this page has not been saved yet and would be lost."
              : "You have changes that have not been saved. If you leave this page now, they will be lost."}
          </p>
        </ConfirmModal>
      )}
    </RegistryContext.Provider>
  );
}

/**
 * Hold the way out of the page while `dirty`. Returns `leave(to, options)`: navigate to `to`
 * WITHOUT asking on behalf of this form — for the navigation that follows a successful save (or a
 * deliberate discard the operator was already warned about).
 */
export function useUnsavedChanges(dirty: boolean): { leave: (to: To, options?: NavigateOptions) => void } {
  const registry = useContext(RegistryContext);
  const navigate = useNavigate();
  const id = useId();

  useEffect(() => {
    registry.report(id, dirty);
    return () => registry.report(id, false);
  }, [registry, id, dirty]);

  const leave = useCallback(
    (to: To, options?: NavigateOptions) => {
      registry.withoutEntry(id, () => {
        void navigate(to, options);
      });
    },
    [registry, id, navigate],
  );

  return { leave };
}

/**
 * Run a gesture that leaves the console without going through the router (signing out), after asking
 * the operator whether to lose what is unsaved. Runs it at once when nothing is.
 */
export function useRequestLeave(): (action: () => void | Promise<unknown>) => void {
  return useContext(RegistryContext).requestLeave;
}
